import { Control213 } from "aic";
import { ReactElement } from "react";
import { Alert } from "react-bootstrap";

/**
 * Props for one mutually exclusive group of named patch radios.
 */
export interface TlfVariantPatchGroupRadioProps {
  patchNames: string[];
  name: string;
  selectedPatchNames: string[];
  disabled: boolean;
  warning?: string;
  onSelectPatch: (patchName: string) => void;
}

/**
 * Renders the None choice, patches and any composition warning for one patch group.
 *
 * @param props - Group identity, selection state and callback supplied by the sidebar
 * @param props.patchNames - Mutually exclusive patch names in the group
 * @param props.name - Accessible heading shown above the choices
 * @param props.selectedPatchNames - Complete current patch selection
 * @param props.disabled - Whether the radio choices are unavailable
 * @param props.warning - Optional warning related to composing this selection
 * @param props.onSelectPatch - Reports the selected patch or empty string for None
 * @returns A labelled fieldset containing mutually exclusive patch radios
 */
export const TlfVariantPatchGroupRadio = ({
  patchNames,
  name,
  selectedPatchNames,
  disabled,
  warning,
  onSelectPatch,
}: TlfVariantPatchGroupRadioProps): ReactElement => {
  const groupId = encodeURIComponent(name);
  return (
    <fieldset
      className="border-bottom pb-2"
      disabled={disabled}
    >
      <legend className="fs-6 fw-semibold text-break">{name}</legend>
      <Control213
        type="radio"
        id={`variant-none-${groupId}`}
        name={`variant-group-${groupId}`}
        label="None"
        checked={!patchNames.some((patchName) => selectedPatchNames.includes(patchName))}
        setChecked={() => onSelectPatch("")}
        disabled={disabled}
        labelWidth={0}
      />
      {patchNames.map((patchName) => (
        <Control213
          key={patchName}
          type="radio"
          id={`variant-patch-${encodeURIComponent(patchName)}`}
          name={`variant-group-${groupId}`}
          label={patchName}
          checked={selectedPatchNames.includes(patchName)}
          setChecked={() => onSelectPatch(patchName)}
          disabled={disabled}
          labelWidth={0}
        />
      ))}
      {warning && (
        <Alert
          variant="danger"
          className="mb-2"
        >
          {warning}
        </Alert>
      )}
    </fieldset>
  );
};

TlfVariantPatchGroupRadio.displayName = "TlfVariantPatchGroupRadio";
